import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bot, MessageSquare, Plus, Search, Sparkles, Trash2 } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { StatCard } from '@/components/StatCard'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import {
  useAgents,
  useAgentTemplates,
  useCloneAgentTemplate,
  useDeleteAgent,
} from '@/features/agents/queries'
import { dataScopeLabel } from '@/features/agents/dataScope'
import type { Agent } from '@/features/agents/types'
import type { Visibility } from '@/features/workflows/types'
import { AgentFormModal } from './AgentFormModal'

type Filter = 'all' | 'mine' | Visibility

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'private', label: 'Private' },
  { value: 'tenant', label: 'Team' },
  { value: 'shared', label: 'Shared' },
]

function visibilityLabel(v: Visibility): string {
  if (v === 'private') return 'Private'
  if (v === 'shared') return 'Shared'
  return 'Team'
}

function fmtRelative(iso?: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`
  return d.toLocaleDateString()
}

export function AgentsPage() {
  const { hasPermission, user } = useAuth()
  const canManage = hasPermission('agents.manage')
  const currentUserId = user?.userId ? Number(user.userId) : null
  const { toast } = useToast()
  const navigate = useNavigate()

  const q = useAgents()
  const removeM = useDeleteAgent()
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const templatesQ = useAgentTemplates(templatesOpen)
  const cloneM = useCloneAgentTemplate()

  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Agent | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Agent | null>(null)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (a: Agent) => {
    setEditing(a)
    setFormOpen(true)
  }

  const agents = useMemo(() => q.data?.agents ?? [], [q.data])

  const stats = useMemo(() => {
    const total = agents.length
    const mine = currentUserId != null
      ? agents.filter((a) => Number(a.created_by) === currentUserId).length
      : 0
    const team = agents.filter((a) => a.visibility === 'tenant').length
    const shared = agents.filter((a) => a.visibility === 'shared').length
    return { total, mine, team, shared }
  }, [agents, currentUserId])

  const filtered = useMemo(() => {
    const qx = search.trim().toLowerCase()
    return agents.filter((a) => {
      if (filter === 'mine' && Number(a.created_by) !== currentUserId) return false
      if ((filter === 'private' || filter === 'tenant' || filter === 'shared') && a.visibility !== filter) return false
      if (!qx) return true
      return (
        (a.name ?? '').toLowerCase().includes(qx) ||
        (a.description ?? '').toLowerCase().includes(qx)
      )
    })
  }, [agents, filter, search, currentUserId])

  const confirmAndDelete = async () => {
    if (!confirmDelete) return
    try {
      await removeM.mutateAsync(confirmDelete.id)
      toast({ title: 'Agent deleted.' })
      setConfirmDelete(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: messageFor(err, ''),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Page title="AI Agents">
        <Card><CardContent className="py-10"><LoadingSpinner /></CardContent></Card>
      </Page>
    )
  }
  if (q.isError) {
    return (
      <Page title="AI Agents">
        <Alert variant="destructive">
          <AlertDescription>Failed to load agents: {messageFor(q.error, '')}</AlertDescription>
        </Alert>
      </Page>
    )
  }

  return (
    <Page
      title="AI Agents"
      description="Specialized assistants with access to your tenant data."
      width="wide"
      actions={
        canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setTemplatesOpen(true)}>
              <Sparkles className="size-4" /> From template
            </Button>
            <Button onClick={openCreate}>
              <Plus className="size-4" /> New agent
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total" value={stats.total} hint="agents you can see" />
        <StatCard label="Mine" value={stats.mine} hint="yours to edit" />
        <StatCard label="Team" value={stats.team} hint="visible to everyone" />
        <StatCard label="Shared" value={stats.shared} hint="picked members only" />
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder="Search agents"
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <Button
                  key={f.value}
                  variant={filter === f.value ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>

          {agents.length === 0 ? (
            <EmptyState
              icon={Bot}
              title="No agents yet"
              description="Create your first AI agent to work with your financial data."
              action={
                canManage ? (
                  <Button onClick={openCreate}>
                    <Plus className="size-4" /> New agent
                  </Button>
                ) : undefined
              }
            />
          ) : filtered.length === 0 ? (
            <EmptyState icon={Search} title="No matches" description="Try a different search or filter." />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((a) => {
                const isMine = currentUserId != null && Number(a.created_by) === currentUserId
                const canEditThis = canManage && (isMine || a.visibility === 'tenant')
                return (
                  <div key={a.id} className="flex flex-col rounded-md border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Bot className="size-5" aria-hidden />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="truncate text-sm font-semibold">{a.name}</p>
                          <Badge variant="secondary">{visibilityLabel(a.visibility)}</Badge>
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                          {a.description || 'No description.'}
                        </p>
                      </div>
                    </div>
                    <dl className="mt-3 grid gap-2 border-t pt-3 text-xs sm:grid-cols-2">
                      <div>
                        <dt className="font-semibold uppercase tracking-wider text-muted-foreground">Last used</dt>
                        <dd>{fmtRelative(a.last_used_at)}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold uppercase tracking-wider text-muted-foreground">Updated</dt>
                        <dd>{fmtRelative(a.updated_at)}</dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="font-semibold uppercase tracking-wider text-muted-foreground">Owner</dt>
                        <dd>
                          {a.created_by_name ?? '-'}
                          {isMine ? <span className="text-muted-foreground"> (you)</span> : null}
                        </dd>
                      </div>
                    </dl>
                    {Array.isArray(a.allowed_tables) && a.allowed_tables.length > 0 ? (
                      <AllowedTablesList tables={a.allowed_tables} />
                    ) : null}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/chatbot?agent_id=${encodeURIComponent(a.id)}`)}
                      >
                        <MessageSquare className="size-4" /> Open chat
                      </Button>
                      {canEditThis ? (
                        <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>
                          Edit
                        </Button>
                      ) : null}
                      {canEditThis ? (
                        <Button size="sm" variant="danger" onClick={() => setConfirmDelete(a)}>
                          <Trash2 className="size-4" /> Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal.Confirm
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete agent?"
        body={
          confirmDelete ? (
            <p>Delete agent <strong>{confirmDelete.name}</strong>? This cannot be undone.</p>
          ) : null
        }
        onConfirm={confirmAndDelete}
        onCancel={() => setConfirmDelete(null)}
        busy={removeM.isPending}
      />

      <AgentFormModal
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        agent={editing}
        onSaved={() => {
          toast({ title: editing ? 'Agent updated.' : 'Agent created.' })
        }}
      />

      <Modal
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        title="Agent templates"
        description="Clone a pre-built agent into your tenant. You can edit the instructions afterwards."
      >
        {templatesQ.isLoading ? (
          <LoadingSpinner />
        ) : templatesQ.data?.templates?.length ? (
          <ul className="space-y-3">
            {templatesQ.data.templates.map((t) => (
              <li key={t.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{t.name}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {t.allowed_tables.map((tbl) => (
                        <Badge
                          key={tbl}
                          variant="secondary"
                          className="normal-case tracking-normal font-medium"
                        >
                          {dataScopeLabel(tbl)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={cloneM.isPending}
                    onClick={async () => {
                      try {
                        await cloneM.mutateAsync({ id: t.id })
                        setTemplatesOpen(false)
                        toast({ title: `Cloned "${t.name}" - edit it to make it yours.` })
                      } catch (err) {
                        toast({
                          variant: 'destructive',
                          title: 'Clone failed',
                          description: messageFor(err, ''),
                        })
                      }
                    }}
                  >
                    Clone
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={Sparkles}
            title="No templates available"
            description="Templates ship with the app - check back on the next release."
          />
        )}
      </Modal>
    </Page>
  )
}

function AllowedTablesList({ tables }: { tables: string[] }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? tables : tables.slice(0, 4)
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      {visible.map((t) => (
        <Badge key={t} variant="outline" className="text-[10px] normal-case tracking-normal font-medium">
          {dataScopeLabel(t)}
        </Badge>
      ))}
      {tables.length > 4 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-1.5 py-0 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `+${tables.length - 4} more`}
        </Button>
      ) : null}
    </div>
  )
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
